import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  clearLocalStorageFieldFilterOptions,
  clearLocalStorageFieldSearchQuery,
  createFieldSearchMatcher,
  doRangesOverlap,
  formatNumber,
  getLocalStorageFieldSearchQuery,
  getLocalStorageShowBaselineYears,
  getLocalStorageShowCropDetails,
  getLocalStorageShowFieldRecommendations,
  getSelfProducedFertilizerIdsByYear,
  getUserFacingApiErrorMessage,
  isFeatureActive,
  isFertilizerSourceComplete,
  isFieldEnrolled,
  setLocalStorageFieldFilterOptions,
  setLocalStorageFieldSearchQuery,
  setLocalStorageShowBaselineYears,
  setLocalStorageShowCropDetails,
  setLocalStorageShowFieldRecommendations,
  toFertilizerSourceValues,
  useAddFarmSubmission,
  useFeatureFlag,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetAllFieldsRecommendations,
  useGetAllUserVerificationFarmTasks,
  useGetBankingDetails,
  useGetDataSubmissionDates,
  useGetFarmFeatureFlags,
  useGetFarmOrganicFertilizerProductionByYear,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldsByFarmId,
  useGetPendingCoverCropDetections,
  useGetPracticeCrops,
  useGetPracticeOrganicFertilizers,
  useGetVerificationEditingRequests,
  useLocation,
  useNavigate,
  usePracticeManagementCropParameters,
  useTranslation,
  useUserContext,
} from "utils";

import { ANIMAL_BASED_FERTILIZER_SLUGS, COUNTRY_CODE, FEATURE_FLAGS, FEATURE_FLAG_SLUGS } from "consts";

import { ACCOUNTING_METHODOLOGY, CROP_TYPE_FCPO, CropItemData, ENROLLMENT_TYPE, PRACTICE_SLUG, TimelineCalendarItemsProps } from "models";

import { Drawer, FieldSearchFilterBar, Flex, FlexPageSpin, MainPage, MetaData, Message as message } from "components";

import { AllFieldsTimeline } from "pages/app/all-field-view/components/AllFieldsTimeline";
import { DataSubmissionBar } from "pages/app/all-field-view/components/DataSubmissionBar";
import { FarmSwitcher } from "pages/app/all-field-view/components/FarmSwitcher";
import { DATA_STATUS, FieldFilterOptions } from "pages/app/all-field-view/components/FieldFilterOptions";
import { FieldFilterOptionsChips } from "pages/app/all-field-view/components/FieldFilterOptionsChips";
import { FIELD_SORT_OPTION, FieldSortOptions } from "pages/app/all-field-view/components/FieldSortOptions";
import { FieldToolbarAddMenu } from "pages/app/all-field-view/components/FieldToolbarAddMenu";
import { InterventionRetentionMessageModal } from "pages/app/all-field-view/components/InterventionRetentionMessageModal";
import { SubmissionSuccessModal } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { TimelineShowOptionsFilter } from "pages/app/all-field-view/components/TimelineShowOptionsFilter";
import { TasksDrawer } from "pages/app/all-field-view/components/tasksDrawer/TasksDrawer";
import { useMissingDataNavigation } from "pages/app/all-field-view/hooks/useMissingDataNavigation";
import { useScrollPositionPersistence } from "pages/app/all-field-view/hooks/useScrollPositionPersistence";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import {
  createCropItems,
  getCropsWithMissingHistoricYieldDataByCropId,
  getCropsWithMissingHistoricalPracticeCount,
  getCropsWithMissingHistoricalPracticeDataByYear,
  getEnrolledFields,
  getEnrolledFieldsCount,
  getFarmOpenTasksCount,
  getMissingDocumentsCounts,
  getMissingHistoricYieldDataOpenTasksCount,
  getSubmissionBarTasksTriggerButtonCount,
  hasItemsWithMissingData,
  sortFieldsBySortOption,
} from "pages/app/all-field-view/utils/AllFieldsUtils";
import { AllFieldsImpactPanel } from "pages/app/all-fields-data-table-view/components/AllFieldsImpactPanel";
import { CropMixComposer } from "pages/app/crop-mix-composer/CropMixComposer.tsx";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { ImportCropsButton } from "pages/app/document-scan/components/ImportCropsButton";
import { createPlaceholderItems } from "pages/app/field-timeline/utils/PlaceholderUtils";
import { createRecommendations } from "pages/app/field-timeline/utils/RecommendationUtils";
import { FieldEnrollmentDrawer } from "pages/app/fields/components/FieldEnrollmentDrawer";
import { FieldUpload } from "pages/app/fields/components/FieldUpload";

export const AllFieldView = () => {
  // loading practices.json here even though it's not used
  // this file is large and causes funky page behaviour if loaded later
  // this way, it's already loaded and available for the client prior to user action
  const { t } = useTranslation(["allFieldView", "fieldTimeline", "practices"]);
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useUserContext();
  const { currentFarm, isLoading: isFarmsLoading } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram;
  const {
    data: { activeFarmReportingYear, isReportingYearLocked },
    isLoading: isFarmReportingYearLoading,
  } = useGetFarmReportingYear(currentFarm?.id || null);

  const { data: fields, isLoading: isLoadingFields } = useGetFieldsByFarmId({ farmId: currentFarm?.id ?? "" });
  const {
    data: carbonProgramOutcomes,
    progress,
    isLoading: isLoadingCarbonProgramOutcomes,
    isFetching: isFetchingCarbonProgramOutcomes,
  } = useGetAllCarbonProgramOutcomes(currentFarm?.id, carbonProgram);
  const {
    data: allFieldsPractices,
    isLoading: isLoadingAllFieldsPractices,
    isFetching: isFetchingAllFieldsPractices,
  } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: verificationFarmTasks } = useGetAllUserVerificationFarmTasks(currentFarm?.id);
  const { data: dataSubmissionDates } = useGetDataSubmissionDates({ farmId: currentFarm?.id });
  const { data: bankingDetails } = useGetBankingDetails({ farmId: currentFarm?.id });
  const { data: crops } = useGetPracticeCrops({});
  const { data: cropParameters } = usePracticeManagementCropParameters();
  const { data: farmFeatureFlags } = useGetFarmFeatureFlags({ farmId: currentFarm?.id ?? "" });
  const isFarmFeatureFlagsLoading = !!currentFarm?.id && !farmFeatureFlags;

  // Applied filter state (what's currently filtering the fields)
  const [appliedCropIds, setAppliedCropIds] = useState<string[]>([]);
  const [appliedSortOption, setAppliedSortOption] = useState<FIELD_SORT_OPTION | null>(null);
  const [appliedDataStatus, setAppliedDataStatus] = useState<DATA_STATUS>(DATA_STATUS.ALL);
  const [appliedYears, setAppliedYears] = useState<number[]>([]);
  const [fieldsInView, setFieldsInView] = useState<string[]>([]);
  const [filteredFieldIds, setFilteredFieldIds] = useState<string[] | null>(null);
  const [searchQuery, setSearchQuery] = useState(() => (currentFarm?.id ? getLocalStorageFieldSearchQuery(currentFarm.id) : ""));
  const [showBaselineYears, setShowBaselineYears] = useState(() => (currentFarm?.id ? getLocalStorageShowBaselineYears(currentFarm.id) : false));
  const [showCropDetails, setShowCropDetails] = useState(() => (currentFarm?.id ? getLocalStorageShowCropDetails(currentFarm.id) : false));
  const [selectedFilterOptionsCount, setSelectedFilterOptionsCount] = useState(0);
  const [showRecommendations, setShowRecommendations] = useState(!!getLocalStorageShowFieldRecommendations());
  const [showBulkEnrollDrawer, setShowBulkEnrollDrawer] = useState<boolean>(false);
  // open the drawer by default if the farm has no fields (not when search/filters just return no results)
  const [showFieldUploadDrawer, setShowFieldUploadDrawer] = useState<boolean>(false);
  const [showCropMixComposer, setShowCropMixComposer] = useState<boolean>(false);
  const [showSubmissionSuccessModal, setShowSubmissionSuccessModal] = useState<boolean>(false);
  const [showTasksDrawer, setShowTasksDrawer] = useState<boolean>(false);
  const [navigateToFirstMissingData, setNavigateToFirstMissingData] = useState<boolean>(false);
  const [showInterventionRetentionMessageModal, setShowInterventionRetentionMessageModal] = useState<boolean | null>(null);

  const { selectedRecommendationItemOnTimeline } = useCropMixComposerContext();
  const { getItemsWithMissingDataByFieldId } = useMissingDataNavigation();
  const { data: verificationEditingRequests } = useGetVerificationEditingRequests({
    farmId: currentFarm?.id ?? "",
  });

  const { data: cropRecommendationsByFieldId, isFetching: isFetchingCropRecommendations } = useGetAllFieldsRecommendations(
    getLocalStorageShowFieldRecommendations() ? fieldsInView : []
  );

  const { mutateAsync: submitData } = useAddFarmSubmission({ farmId: currentFarm?.id ?? "" });
  const { isDataSubmitted, isDataSubmissionReopened, isSubmissionFeatureEnabled } = useSubmissionStatus(activeFarmReportingYear);

  const { scrollContainerRef, saveScrollPosition } = useScrollPositionPersistence(
    `field-list-scroll-${currentFarm?.id || "default"}`,
    !isLoadingFields && !!fields?.length
  );

  const isCropMixComposerPath = location.pathname.includes("/cmc");

  const isEnabledProgramFarm = !!carbonProgram;
  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const isAutoEnrollmentType = carbonProgram?.fieldsManagement?.fieldEnrollmentType === ENROLLMENT_TYPE.auto;

  const { isEnabled: isSoilSightEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);
  const { isEnabled: isOrganicFertilizerSourcesEnabled } = useFeatureFlag(FEATURE_FLAGS.ORGANIC_FERTILIZER_SOURCES);
  const { isEnabled: isViewSwitcherEnabled } = useFeatureFlag(FEATURE_FLAGS.FIELD_MANAGEMENT_VIEW_SWITCHER);
  const { isEnabled: isCoverCropDetectionEnabled } = useFeatureFlag(FEATURE_FLAGS.COVER_CROP_DETECTION, { project: "common" });
  const shouldShowInsightsImpactPanel = isSoilSightEnabled && !!carbonProgram?.platformFeatures?.fieldManagement?.insightsImpactPanel;

  const isSoilPlus3DataGapFeatureActive = isFeatureActive(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP, farmFeatureFlags);
  const isSoilPlus3RewardsFeatureActive = isFeatureActive(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_REWARDS, farmFeatureFlags);

  const { totalMissingDocumentsCount, openMissingDocumentsCount } = getMissingDocumentsCounts(verificationFarmTasks, carbonProgram);

  const enrolledFieldsCount = getEnrolledFieldsCount(fields ?? [], carbonProgramOutcomes ?? {}, carbonProgram ?? null, allFieldsPractices);
  // farms with at least one field enrolled with a practice participation that started in the past (before the current reporting year)
  const isRetentionFarm = activeFarmReportingYear
    ? Object.values(allFieldsPractices || {}).some((practice) => {
        const isEnrolled = isFieldEnrolled(practice.availablePracticeBundles, carbonProgram ?? null);
        return (
          isEnrolled &&
          practice.availablePracticeBundles.some(
            (bundle) => bundle.slug === PRACTICE_SLUG.CROPS && bundle.startYear && bundle.startYear < activeFarmReportingYear
          )
        );
      })
    : false;

  const matchesSearch = useMemo(() => createFieldSearchMatcher(searchQuery), [searchQuery]);
  const filteredFields = useMemo(() => {
    const availableFields = filteredFieldIds === null ? fields : fields?.filter((field) => filteredFieldIds.includes(field.id));
    return availableFields?.filter(matchesSearch) ?? [];
  }, [fields, matchesSearch, filteredFieldIds]);

  const enrolledFieldIds = useMemo(() => {
    const enrolledIds = new Set<string>();
    Object.entries(allFieldsPractices || {}).forEach(([fieldId, fieldPracticeResponse]) => {
      if (isFieldEnrolled(fieldPracticeResponse?.availablePracticeBundles ?? [], carbonProgram ?? null)) enrolledIds.add(fieldId);
    });
    return enrolledIds;
  }, [allFieldsPractices, carbonProgram]);

  const sortedFilteredFields = useMemo(
    () => sortFieldsBySortOption(filteredFields, appliedSortOption, enrolledFieldIds),
    [filteredFields, appliedSortOption, enrolledFieldIds]
  );

  const activeField = fields?.find((field) => field.id === selectedRecommendationItemOnTimeline?.group);

  const timelineItems = useMemo(() => {
    if (!carbonProgramOutcomes || !fields || fields.length === 0 || isLoadingAllFieldsPractices || !activeFarmReportingYear) return [];

    return Object.entries(allFieldsPractices || {}).reduce((acc, [fieldId, fieldPracticeResponse]) => {
      const field = fields?.find((field) => field.id === fieldId);
      if (!field || !fieldPracticeResponse) return acc;

      const cropBaselineYears = field.baselineYears ?? [];
      const isEnrolled = isFieldEnrolled(fieldPracticeResponse.availablePracticeBundles, carbonProgram ?? null);

      const cropItems = createCropItems(fieldPracticeResponse, fieldId, cropBaselineYears, isEnrolled, t);
      const cropPlaceholderItems = createPlaceholderItems(
        fieldPracticeResponse,
        cropBaselineYears,
        isEnrolled,
        carbonProgram ?? null,
        activeFarmReportingYear,
        fieldId,
        verificationEditingRequests,
        isSoilPlus3DataGapFeatureActive
      );

      // create CMC placeholder cards starting from cover crops placeholder cards for years with a main crop
      const coverCropPlaceholderItems = cropPlaceholderItems.filter(
        (placeholder) =>
          placeholder.data.cropType === CROP_TYPE_FCPO.COVER &&
          cropItems.some((crop) => crop.data.cropType === CROP_TYPE_FCPO.MAIN && crop.data.year === placeholder.data.year)
      );
      const recommendationPlaceholderItems = cropRecommendationsByFieldId?.[fieldId]
        ? createRecommendations(cropRecommendationsByFieldId[fieldId] ?? [], coverCropPlaceholderItems, fieldId, fieldId)
        : [];

      if (showRecommendations && recommendationPlaceholderItems.length > 0) {
        const filteredPlaceholderItems = cropPlaceholderItems.filter((crop) => {
          if (crop.data?.cropType === CROP_TYPE_FCPO.MAIN) return true;

          const overlappingPlaceholder = recommendationPlaceholderItems.some((recommendation) =>
            doRangesOverlap(recommendation.start_time, recommendation.end_time, crop.start_time, crop.end_time)
          );
          return !overlappingPlaceholder;
        });

        acc.push(...cropItems, ...filteredPlaceholderItems, ...recommendationPlaceholderItems);
        return acc;
      }

      acc.push(...cropItems, ...cropPlaceholderItems);
      return acc;
    }, [] as TimelineCalendarItemsProps<CropItemData>[]);
  }, [
    activeFarmReportingYear,
    carbonProgramOutcomes,
    carbonProgram,
    fields,
    isLoadingAllFieldsPractices,
    allFieldsPractices,
    t,
    cropRecommendationsByFieldId,
    showRecommendations,
    verificationEditingRequests,
    isSoilPlus3DataGapFeatureActive,
  ]);

  const itemsWithMissingDataByFieldId = useMemo(
    () =>
      getItemsWithMissingDataByFieldId({
        fields: fields ?? [],
        carbonProgramOutcomesResponse: carbonProgramOutcomes,
        allFieldsPractices,
        timelineItems,
        carbonProgram: carbonProgram ?? null,
        verificationEditingRequests,
        dataSubmissionDates,
        reportingYear: activeFarmReportingYear ?? moment().year(),
        crops,
        cropParameters,
        submissionFeatureFlag: isSubmissionFeatureEnabled,
      }),
    [
      activeFarmReportingYear,
      allFieldsPractices,
      carbonProgram,
      carbonProgramOutcomes,
      cropParameters,
      crops,
      dataSubmissionDates,
      fields,
      getItemsWithMissingDataByFieldId,
      isSubmissionFeatureEnabled,
      timelineItems,
      verificationEditingRequests,
    ]
  );

  const cropsWithMissingHistoricYieldDataByCropId = useMemo(
    () =>
      getCropsWithMissingHistoricYieldDataByCropId(
        fields ?? [],
        carbonProgramOutcomes,
        allFieldsPractices,
        carbonProgram ?? null,
        activeFarmReportingYear,
        crops
      ),
    [fields, carbonProgramOutcomes, allFieldsPractices, carbonProgram, activeFarmReportingYear, crops]
  );

  const handleShowBaselineYearsChange = (checked: boolean) => {
    setShowBaselineYears(checked);
    if (currentFarm?.id) setLocalStorageShowBaselineYears(currentFarm.id, checked);
  };

  // re-read on farm load/switch, since the stored preference is per farm and currentFarm may not be ready on mount
  useEffect(() => {
    if (currentFarm?.id) setShowCropDetails(getLocalStorageShowCropDetails(currentFarm.id));
  }, [currentFarm?.id]);

  const handleShowCropDetailsChange = (checked: boolean) => {
    // kept synchronous so the checkbox itself reflects the click instantly — AllFieldsTimeline defers its own,
    // much heavier consumption of this value (every row's height, every main crop's details panel) separately
    setShowCropDetails(checked);
    if (currentFarm?.id) setLocalStorageShowCropDetails(currentFarm.id, checked);
  };

  const handleShowRecommendationsChange = (checked: boolean) => {
    setShowRecommendations(checked);
    setLocalStorageShowFieldRecommendations(checked);
  };

  const { fieldsWithOpenTasksCount, openTasksCount } = getFarmOpenTasksCount(itemsWithMissingDataByFieldId, verificationEditingRequests ?? []);
  const missingHistoricYieldDataOpenTasksCount = getMissingHistoricYieldDataOpenTasksCount(cropsWithMissingHistoricYieldDataByCropId);

  const handleClearFilters = () => {
    setFilteredFieldIds(null);
    setSearchQuery("");
    setSelectedFilterOptionsCount(0);
    setAppliedCropIds([]);
    setAppliedYears([]);
    setAppliedDataStatus(DATA_STATUS.ALL);
    clearLocalStorageFieldFilterOptions();
    clearLocalStorageFieldSearchQuery();
  };

  const applyMissingDataFilter = useCallback(
    (missingFieldIds: string[]) => {
      const farmId = currentFarm?.id;
      if (!farmId) return;

      // Clear other filters and apply "Missing data" status
      setSearchQuery("");
      setAppliedCropIds([]);
      setAppliedYears([]);
      setAppliedDataStatus(DATA_STATUS.MISSING);
      setSelectedFilterOptionsCount(1);
      setFilteredFieldIds(missingFieldIds);

      setLocalStorageFieldFilterOptions({
        farmId,
        cropIds: [],
        years: [],
        dataStatus: DATA_STATUS.MISSING,
      });
    },
    [currentFarm?.id]
  );

  const handleStartMissingDataTask = useCallback(() => {
    if (!currentFarm?.id) return;

    const missingFieldIds = Object.entries(itemsWithMissingDataByFieldId)
      .filter(([, items]) => hasItemsWithMissingData(items))
      .map(([fieldId]) => fieldId);

    applyMissingDataFilter(missingFieldIds);

    // Scroll to top instantly (avoid racing with horizontal navigation)
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });

    // Trigger horizontal navigation after React renders the filtered state
    requestAnimationFrame(() => setNavigateToFirstMissingData(true));
  }, [currentFarm?.id, itemsWithMissingDataByFieldId, applyMissingDataFilter, scrollContainerRef]);

  useEffect(() => {
    if (location.pathname.includes("/cmc") && !selectedRecommendationItemOnTimeline) {
      const basePath = location.pathname.split("/cmc").slice(0, -1).join("/");
      navigate(basePath);
    }
  }, [location.pathname, navigate, selectedRecommendationItemOnTimeline]);

  // this is used to programmatically open the tasks drawer when the user navigates
  // to the all-fields page from the historical practice data table view
  useEffect(() => {
    if (location.state?.openTasksDrawer) {
      setShowTasksDrawer(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Restore search query from localStorage when farm is known
  useEffect(() => {
    if (!currentFarm?.id) return;
    const saved = getLocalStorageFieldSearchQuery(currentFarm.id);
    if (saved) setSearchQuery(saved);
  }, [currentFarm?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist search query to localStorage on change
  useEffect(() => {
    if (!currentFarm?.id) return;
    setLocalStorageFieldSearchQuery(currentFarm.id, searchQuery);
  }, [searchQuery, currentFarm?.id]);

  const navigationCrumbs = {
    crumbs: [{ name: "", content: <FarmSwitcher />, replacedByFarmSwitch: true }, { name: t("fieldTimeline:header.fieldManagementCrumb") }],
  };

  const fieldSizes = useMemo(
    () =>
      fields?.reduce((fieldSizes: Record<string, number>, field) => {
        fieldSizes[field.id] = field.size ?? 0;
        return fieldSizes;
      }, {}) || {},
    [fields]
  );

  // filter-scoped counterpart to enrolledFieldsCount above, which stays farm-wide for the Impact Panel
  const visibleEnrolledFields = useMemo(
    () => getEnrolledFields(sortedFilteredFields, carbonProgramOutcomes ?? {}, carbonProgram ?? null, allFieldsPractices),
    [sortedFilteredFields, carbonProgramOutcomes, carbonProgram, allFieldsPractices]
  );
  const visibleEnrolledFieldsTotalHectares = useMemo(
    () => visibleEnrolledFields.reduce((total, field) => total + (fieldSizes[field.id] ?? 0), 0),
    [visibleEnrolledFields, fieldSizes]
  );

  const visibleFieldsSizeSummary = useMemo(() => {
    const allFieldsTotalHectares = Object.values(fieldSizes).reduce((total, size) => total + size, 0);
    if (!allFieldsTotalHectares) return null;

    const visibleFieldsTotalHectares = sortedFilteredFields.reduce((total, field) => total + (fieldSizes[field.id] ?? 0), 0);
    const sharePercent = Math.round((visibleFieldsTotalHectares / allFieldsTotalHectares) * 100);

    return t("allFieldView:fieldsSizeSummary", { size: formatNumber({ number: visibleFieldsTotalHectares, decimalPlaces: 1 }), percent: sharePercent });
  }, [fieldSizes, sortedFilteredFields, t]);

  const handleSubmitClick = async () => {
    if (!currentFarm?.id || !carbonProgramOutcomes) return;

    try {
      const { success } = await submitData({ farmId: currentFarm.id });
      if (success) {
        setShowSubmissionSuccessModal(true);
      }
    } catch (error) {
      message({ content: getUserFacingApiErrorMessage(error, t("allFieldView:submission.error")), icon: "error" });
      // eslint-disable-next-line no-console
      console.error("Submission failed:", error);
    }
  };

  const cropsWithMissingHistoricalPracticeDataByCropId = getCropsWithMissingHistoricalPracticeDataByYear(
    fields ?? [],
    carbonProgramOutcomes,
    carbonProgram ?? null,
    allFieldsPractices,
    activeFarmReportingYear
  );

  const hasSelfProducedOrganicFertilizers =
    isOrganicFertilizerSourcesEnabled &&
    Object.values(allFieldsPractices || {}).some(
      (fieldPractices) => fieldPractices?.practices?.some((app) => app.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && app.selfProduced === true)
    );

  const selfProducedFertilizerIdsByYear = isOrganicFertilizerSourcesEnabled ? getSelfProducedFertilizerIdsByYear(allFieldsPractices) : {};
  const farmSourcesYears = Object.keys(selfProducedFertilizerIdsByYear).map(Number);
  const { productionsByYear: organicFertilizerProductionsByYear } = useGetFarmOrganicFertilizerProductionByYear({
    farmId: currentFarm?.id ?? "",
    years: farmSourcesYears,
  });
  const { data: practiceOrganicFertilizers } = useGetPracticeOrganicFertilizers({});

  // Same years and completeness rule as the farm sources drawer tabs, from saved data only
  const openFarmSourcesTasksCount = farmSourcesYears.reduce((total, year) => {
    const productions = organicFertilizerProductionsByYear[year];
    if (!productions) return total;
    return (
      total +
      selfProducedFertilizerIdsByYear[year].filter((fertilizerId) => {
        const fertilizer = practiceOrganicFertilizers?.find((f) => f.id === fertilizerId);
        const isAnimalBased = fertilizer ? ANIMAL_BASED_FERTILIZER_SLUGS.has(fertilizer.translationSlug) : false;
        return !isFertilizerSourceComplete(toFertilizerSourceValues(productions, fertilizerId), isAnimalBased);
      }).length
    );
  }, 0);

  const { data: pendingCoverCropDetections } = useGetPendingCoverCropDetections({ farmId: isCoverCropDetectionEnabled ? currentFarm?.id : undefined });
  const openCoverCropDetectionsCount = pendingCoverCropDetections?.length ?? 0;

  const fieldsWithOutstandingRedzones = fields?.filter((field) => field.redzones?.some((redzone) => redzone.confirmed === null)) ?? [];
  // only German farms are affected by redzone regulation, and the task is hidden for farms without any redzone fields
  const isRedzoneTaskActive = user?.farm?.country === COUNTRY_CODE.GERMANY && !!fields?.some((field) => field.isInRedzone);
  const shouldShowSubmissionSuccessModalContent = isDataSubmitted && !isDataSubmissionReopened;
  const shouldShowSubmissionBar = isSubmissionFeatureEnabled && isEnabledProgramFarm && enrolledFieldsCount > 0;
  const isBankingDetailsTaskActive = !!bankingDetails?.isReviewRequired;
  const openBankingDetailsTasksCount = isBankingDetailsTaskActive && !bankingDetails?.isReviewedForCurrentSubmission ? 1 : 0;
  const openMissingHistoricalPracticeDataTasksCount = getCropsWithMissingHistoricalPracticeCount(cropsWithMissingHistoricalPracticeDataByCropId);

  const submissionBarTasksTriggerButtonCount = getSubmissionBarTasksTriggerButtonCount({
    isSoilPlus3DataGapFeatureActive,
    isRetentionFarm,
    isInventoryProgram,
    missingHistoricYieldDataOpenTasksCount,
    openMissingDataTasksCount: openTasksCount,
    openMissingDocumentsCount,
    openMissingHistoricalPracticeDataTasksCount,
    openFarmSourcesTasksCount,
    openCoverCropDetectionsCount,
    openRedzoneTasksCount: isRedzoneTaskActive ? fieldsWithOutstandingRedzones.length : 0,
    openBankingDetailsTasksCount,
  });

  const isInterventionRetentionMessageModalDismissed =
    currentFarm?.farmSettings?.find((setting) => setting.item === "interventionRetentionMessageModalDismissed")?.value === "true";
  const shouldShowInterventionRetentionMessageModal =
    !isSoilPlus3DataGapFeatureActive || isInventoryProgram || !isRetentionFarm || isFarmsLoading || isFarmReportingYearLoading || isCropMixComposerPath
      ? false
      : showInterventionRetentionMessageModal !== null
      ? showInterventionRetentionMessageModal
      : !isInterventionRetentionMessageModalDismissed;

  return (
    <MainPage fullWidth loading={isLoadingFields} pageName="all-field-page" navigationCrumbs={navigationCrumbs} showMobileAlert>
      <MetaData title={t("allFieldView")} />
      <Flex vertical>
        <FieldSearchFilterBar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchDisabled={!fields?.length && !isLoadingFields}
          leadingActions={
            <FieldToolbarAddMenu
              fields={fields}
              carbonProgram={carbonProgram}
              isSoilPlus3DataGapFeatureActive={isSoilPlus3DataGapFeatureActive}
              isDataSubmitted={isDataSubmitted}
              fieldsWithOpenTasksCount={fieldsWithOpenTasksCount}
              onAddFields={() => setShowFieldUploadDrawer(true)}
              onEnrollFields={() => setShowBulkEnrollDrawer(true)}
            />
          }
          filters={
            <>
              <FieldSortOptions
                appliedSortOption={appliedSortOption}
                setAppliedSortOption={setAppliedSortOption}
                farmId={currentFarm?.id || ""}
                isDisabled={!fields?.length}
                isAutoEnrollmentType={isAutoEnrollmentType}
              />
              {isViewSwitcherEnabled ? (
                <FieldFilterOptionsChips
                  fields={fields ?? []}
                  filteredFieldIds={filteredFieldIds}
                  setFilteredFieldIds={setFilteredFieldIds}
                  setSelectedFilterOptionsCount={setSelectedFilterOptionsCount}
                  appliedCropIds={appliedCropIds}
                  appliedYears={appliedYears}
                  appliedDataStatus={appliedDataStatus}
                  setAppliedCropIds={setAppliedCropIds}
                  setAppliedYears={setAppliedYears}
                  setAppliedDataStatus={setAppliedDataStatus}
                  farmId={currentFarm?.id || ""}
                  handleClearFilters={handleClearFilters}
                  itemsWithMissingDataByFieldId={itemsWithMissingDataByFieldId}
                />
              ) : (
                <FieldFilterOptions
                  fields={fields ?? []}
                  filteredFieldIds={filteredFieldIds}
                  setFilteredFieldIds={setFilteredFieldIds}
                  selectedFilterOptionsCount={selectedFilterOptionsCount}
                  setSelectedFilterOptionsCount={setSelectedFilterOptionsCount}
                  appliedCropIds={appliedCropIds}
                  appliedYears={appliedYears}
                  appliedDataStatus={appliedDataStatus}
                  setAppliedCropIds={setAppliedCropIds}
                  setAppliedYears={setAppliedYears}
                  setAppliedDataStatus={setAppliedDataStatus}
                  farmId={currentFarm?.id || ""}
                  handleClearFilters={handleClearFilters}
                  itemsWithMissingDataByFieldId={itemsWithMissingDataByFieldId}
                />
              )}
            </>
          }
          trailingActions={
            <>
              {!!fields?.length && (
                <TimelineShowOptionsFilter
                  showCropDetails={showCropDetails}
                  onShowCropDetailsChange={handleShowCropDetailsChange}
                  showBaselineYears={carbonProgram && !isInventoryProgram ? showBaselineYears : undefined}
                  onShowBaselineYearsChange={carbonProgram && !isInventoryProgram ? handleShowBaselineYearsChange : undefined}
                  showRecommendations={fields?.length ? showRecommendations : undefined}
                  onShowRecommendationsChange={fields?.length ? handleShowRecommendationsChange : undefined}
                  isRecommendationsDisabled={isFetchingCropRecommendations}
                />
              )}
              <ImportCropsButton />
            </>
          }
        />
        <Flex className="all-field-view container" data-testid="all-field-view-page-container" justify="space-between">
          <div className="main-container-wrapper">
            <Flex vertical className={`main-container${shouldShowSubmissionBar ? " with-submission-bar" : ""}`} ref={scrollContainerRef}>
              <div className="timeline-container">
                {isFarmReportingYearLoading || !activeFarmReportingYear ? (
                  <FlexPageSpin />
                ) : (
                  <AllFieldsTimeline
                    allFields={fields ?? []}
                    filteredFields={sortedFilteredFields}
                    allFieldsPractices={allFieldsPractices || {}}
                    itemsWithMissingDataByFieldId={itemsWithMissingDataByFieldId}
                    timelineItems={timelineItems}
                    carbonProgramOutcomesResponse={carbonProgramOutcomes}
                    isLoadingCarbonProgramOutcomes={isLoadingCarbonProgramOutcomes}
                    fieldsInView={fieldsInView}
                    setFieldsInView={setFieldsInView}
                    cropRecommendationsByFieldId={cropRecommendationsByFieldId}
                    farmFieldsCount={fields?.length || 0}
                    enrolledFieldsCount={visibleEnrolledFields.length}
                    enrolledFieldsTotalHectares={visibleEnrolledFieldsTotalHectares}
                    isEnrollmentDataLoading={isFetchingAllFieldsPractices}
                    fieldsSizeSummary={visibleFieldsSizeSummary}
                    showBaselineYears={showBaselineYears}
                    showCropDetails={showCropDetails}
                    hasAppliedFilters={!!searchQuery || selectedFilterOptionsCount > 0}
                    handleClearFilters={handleClearFilters}
                    onFieldNavigate={saveScrollPosition}
                    verificationEditingRequests={verificationEditingRequests}
                    reportingYear={activeFarmReportingYear}
                    isReportingYearLocked={isReportingYearLocked}
                    shouldShowIndividualFieldPayouts={!shouldShowSubmissionSuccessModalContent}
                    useMinimumPayoutForFieldCells={!!isSoilPlus3RewardsFeatureActive}
                    navigateToFirstMissingData={navigateToFirstMissingData}
                    onNavigateToFirstMissingDataHandled={() => setNavigateToFirstMissingData(false)}
                    onShowFieldUpload={() => setShowFieldUploadDrawer(true)}
                  />
                )}
              </div>
            </Flex>
          </div>
          <AllFieldsImpactPanel
            isHidden={
              !carbonProgram?.id ||
              (fields?.length ?? currentFarm?.fieldCount ?? 0) === 0 ||
              (!shouldShowInsightsImpactPanel && !carbonProgram?.platformFeatures?.fieldManagement?.impactPanel)
            }
            isLoading={isFarmReportingYearLoading || isFarmFeatureFlagsLoading}
            className={shouldShowSubmissionBar ? "with-submission-bar" : ""}
            reportingYear={activeFarmReportingYear ?? new Date().getFullYear()}
            farmId={currentFarm?.id ?? ""}
            carbonProgram={carbonProgram}
            carbonProgramOutcomes={carbonProgramOutcomes}
            progress={progress}
            fieldSizes={fieldSizes}
            allFieldsPractices={allFieldsPractices}
            isFetchingOutcomes={isFetchingCarbonProgramOutcomes}
            isFetchingPractices={isFetchingAllFieldsPractices}
            enrolledFieldsCount={enrolledFieldsCount}
            shouldShowInsightsImpactPanel={shouldShowInsightsImpactPanel}
            shouldShowSoilPlus3ImpactPanel={!!isSoilPlus3RewardsFeatureActive}
            shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
            onOpenBulkEnrollDrawer={() => setShowBulkEnrollDrawer(true)}
            onNavigateToMissingData={() => setShowTasksDrawer(true)}
          />
        </Flex>
        <FieldEnrollmentDrawer fields={fields ?? []} visible={showBulkEnrollDrawer} onClose={() => setShowBulkEnrollDrawer(false)} />
        <Drawer className="field-upload-drawer" visible={showFieldUploadDrawer} title={t("titleUpdateDrawer")} onClose={() => setShowFieldUploadDrawer(false)}>
          <FieldUpload onFinish={() => setShowFieldUploadDrawer(false)} />
        </Drawer>
        {shouldShowSubmissionBar && (
          <>
            <DataSubmissionBar
              enrolledFieldsCount={enrolledFieldsCount}
              fieldsWithMissingTasksCount={fieldsWithOpenTasksCount}
              openTasksCount={openTasksCount}
              openMissingDocumentsCount={openMissingDocumentsCount}
              totalMissingDocumentsCount={totalMissingDocumentsCount}
              tasksTriggerButtonCount={submissionBarTasksTriggerButtonCount}
              openBankingDetailsTasksCount={openBankingDetailsTasksCount}
              onOpenTasksClick={() => setShowTasksDrawer(true)}
              onSubmitClick={handleSubmitClick}
            />
            <TasksDrawer
              visible={showTasksDrawer}
              onClose={() => setShowTasksDrawer(false)}
              openMissingDataTasksCount={openTasksCount}
              openMissingDocumentsCount={openMissingDocumentsCount}
              openMissingHistoricalPracticeDataTasksCount={openMissingHistoricalPracticeDataTasksCount}
              onStartMissingDataTask={handleStartMissingDataTask}
              cropsWithMissingHistoricYieldDataByCropId={cropsWithMissingHistoricYieldDataByCropId}
              missingHistoricYieldDataOpenTasksCount={missingHistoricYieldDataOpenTasksCount}
              onSubmitClick={handleSubmitClick}
              hasSelfProducedOrganicFertilizers={hasSelfProducedOrganicFertilizers}
              openFarmSourcesTasksCount={openFarmSourcesTasksCount}
              openCoverCropDetectionsCount={openCoverCropDetectionsCount}
              isRedzoneTaskActive={isRedzoneTaskActive}
              fieldsWithOutstandingRedzones={fieldsWithOutstandingRedzones}
              isRetentionFarm={isRetentionFarm}
              isBankingDetailsTaskActive={isBankingDetailsTaskActive}
              openBankingDetailsTasksCount={openBankingDetailsTasksCount}
            />
          </>
        )}
      </Flex>
      <CropMixComposer
        visible={showCropMixComposer || isCropMixComposerPath}
        setShowCropMixComposer={setShowCropMixComposer}
        regionId={activeField?.regionId}
        openInDrawer
        fieldId={activeField?.id}
      />
      {showSubmissionSuccessModal && shouldShowSubmissionSuccessModalContent && activeFarmReportingYear && (
        <SubmissionSuccessModal
          carbonProgramOutcomes={carbonProgramOutcomes ?? null}
          farmId={currentFarm?.id ?? ""}
          visible={showSubmissionSuccessModal}
          onClose={() => setShowSubmissionSuccessModal(false)}
        />
      )}
      {user?.farm?.country === COUNTRY_CODE.GERMANY && (
        <InterventionRetentionMessageModal isOpen={shouldShowInterventionRetentionMessageModal} setIsOpen={setShowInterventionRetentionMessageModal} />
      )}
    </MainPage>
  );
};
